<template>
  <div
    :aria-describedby="describedBy"
    :aria-labelledby="label || hasLabelSlot ? labelId : undefined"
    class="input-container"
    :class="newDesign && 'new-design'"
    :role="label || hasLabelSlot ? 'group' : undefined"
  >
    <slot name="before" />
    <div
      v-if="hasLabelSlot"
      :id="labelId"
      data-testid="input-label"
    >
      <slot name="label" />
    </div>

    <template v-else>
      <label
        v-if="label"
        :id="labelId"
        class="block"
        :class="{
          'field-heading': newDesign,
          'q-mb-sm': !description,
          'q-mb-xs': description,
        }"
        data-testid="input-label"
        :for="inputId || undefined"
      >
        {{ label }}
        <slot name="appendLabel" />
      </label>

      <div
        v-if="description"
        :id="descriptionId"
        class="field-subtext q-mb-sm"
        data-testid="field-description"
      >
        {{ description }}
      </div>
    </template>
    <div
      class="input-wrapper"
      :class="{ prepend: hasPrependSlot, append: hasAppendSlot }"
    >
      <slot name="prepend" />

      <div
        ref="control"
        class="full-width"
        tabindex="-1"
      >
        <slot name="control" />
      </div>

      <slot name="append" />
    </div>
    <slot name="after" />

    <div
      v-if="errors.length"
      class="input-container__errors"
    >
      <span
        :id="errorId"
        class="text-help-text"
        data-testid="input-errors"
        role="alert"
      >
        {{ errors[0] }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "BaseInputWrapper",
};
</script>

<script setup>
import {
  computed,
  getCurrentInstance,
  ref,
  toRef,
  useId,
  useSlots,
  watch,
} from "vue";

import useValidation from "shared/composables/useValidation";

const props = defineProps({
  modelValue: {
    // eslint-disable-next-line vue/prefer-prop-type-boolean-first
    type: [String, Boolean, Number, Object, Array, Date],
    default: null,
  },
  validations: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  inputId: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  optional: {
    type: Boolean,
  },
  newDesign: {
    type: Boolean,
  },
});

defineEmits(["update:modelValue"]);
const slots = useSlots();
const labelId = useId();

const { input, errors, isValid } = useValidation({
  modelValue: toRef(() => props.modelValue),
  validations: toRef(() => props.validations),
  optional: toRef(() => props.optional),
});

const { proxy } = getCurrentInstance();
const control = ref(null);

const hasPrependSlot = computed(() => Boolean(slots.prepend));
const hasAppendSlot = computed(() => Boolean(slots.append));
const hasLabelSlot = computed(() => Boolean(slots.label));

const descriptionId = computed(() =>
  !hasLabelSlot.value && props.description ? `${labelId}-desc` : undefined
);

const errorId = computed(() =>
  errors.value.length ? `${labelId}-error` : undefined
);

const describedBy = computed(
  () =>
    [descriptionId.value, errorId.value].filter(Boolean).join(" ") || undefined
);

const findControlComponent = () => control.value.querySelector("[tabindex]");

/** @public */
const focus = () => {
  const component = findControlComponent();
  if (component?.focus) component.focus();
};

/** @public */
const blur = () => {
  const component = findControlComponent();
  if (component?.blur) component.blur();
};

Object.assign(proxy, {
  focus,
  blur,
});

watch(
  () => props.modelValue,
  (modelValue) => {
    input.value = modelValue;
  },
  { deep: true }
);

defineExpose({
  isValid,
  labelId,
});
</script>

<style lang="scss" scoped>
.input-container:not(.new-design) {
  .input-wrapper {
    display: flex;
    align-items: center;
    padding: 0;

    &.prepend {
      padding-left: 16px;
    }

    &.append {
      padding-right: 16px;
    }
  }
}

.new-design {
  .input-wrapper {
    --c-input-height: inherit;
    --c-input-padding-inline: inherit;

    border: inherit;
  }
}

.text-help-text {
  color: var(--s-color-danger);
}
</style>
